import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { BackendService } from './backend.service';
import { CONFIRM_SIGNUP_REQUEST, LOGIN_REQUEST, LOGOUT_REQUEST, WHOAMI_REQUEST } from './gql_queries/user.service.gql';
import { HttpClient } from '@angular/common/http';

export interface LoginResponse {
  token?: string;
  expiryAt?: string;
  username?: string;
  userId?: string;
  email?: string;
  groups?: string[];
  error?: string;
}

export interface LogoutResponse {
  error?: string;
}

export interface LoggedInUserInfo {
  username: string,
  userId: string,
  email: string,
  groups: string[],
  token: string,
  expiryAt: string
}

interface LoginResponseFromServer {
  error?: string,
  data?: {
    login: LoginResponse
  }
}

interface LogoutResponseFromServer {
  error?: string,
  data?: {
    logout: LogoutResponse
  }
}

interface WhoAmIResponseFromServer {
  error?: string,
  data?: {
    whoAmI: LoginResponse
  }
}

export interface ConfirmResponse {
  error?: string,
  email?: string,
}

interface ConfirmResponseFromServer {
  error?: string,
  data?: {
    confirmUserSignUp: {
      error?: string,
      email?: string
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private userLoggedInSubject = new BehaviorSubject<LoggedInUserInfo | null>(null);
  userLoggedIn$ = this.userLoggedInSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly backendService: BackendService
  ) {
  }

  setUserLoggedIn(user: LoggedInUserInfo | null) {
    this.userLoggedInSubject.next(user);
  }

  isUserLoggedIn(): boolean {
    return this.userLoggedInSubject.value != null;
  }

  getUserLoggedIn(): LoggedInUserInfo | null {
    return this.userLoggedInSubject.value;
  }

  async initUser(): Promise<void> {
    try {
      const result = await firstValueFrom(this.whoAmI());
      if (result.data?.whoAmI?.token) {
        this.setUserLoggedIn({
          username: result.data.whoAmI.username ?? '',
          token: result.data.whoAmI.token,
          expiryAt: result.data.whoAmI.expiryAt ?? '',
          userId: result.data.whoAmI.userId ?? '',
          email: result.data.whoAmI.email ?? '',
          groups: result.data.whoAmI.groups ?? []
        });
        this.backendService.setAuthToken(result.data.whoAmI.token);
        console.log(`User logged in: ${result.data.whoAmI.username ?? ''}`);
      }
    } catch (err) {
      console.error('Init user failed', err);
    }
  }

  async confirmSignUp(refCode: string, code: string): Promise<ConfirmResponse> {
    const result = await this.backendService.executeGraphQL(
      CONFIRM_SIGNUP_REQUEST,
      {refCode, code}
    ) as ConfirmResponseFromServer;

    if (result.data?.confirmUserSignUp.error != null) {
      return {
        error: result.data.confirmUserSignUp.error
      };
    }
    if (result.error != null) {
      return {
        error: result.error
      };
    }
    return {
      email: result.data?.confirmUserSignUp.email
    }
  }

  whoAmI(): Observable<WhoAmIResponseFromServer> {
    return this.http.post<WhoAmIResponseFromServer>('/graphql', WHOAMI_REQUEST);
  }

  async logout(): Promise<LogoutResponse> {
    const result = await this.backendService.executeGraphQL(
      LOGOUT_REQUEST,
      {}
    ) as LogoutResponseFromServer;

    if (result.data?.logout.error != null) {
      return {
        error: result.data.logout.error
      };
    }
    if (result.error != null) {
      return {
        error: result.error
      };
    }
    this.setUserLoggedIn(null);
    this.backendService.setAuthToken(null);
    return {}
  }

  async logIn(username: string, password: string): Promise<LoginResponse> {
    this.backendService.setAuthToken(null);
    const result = await this.backendService.executeGraphQL(
      LOGIN_REQUEST,
      {
        username, password
      }
    ) as LoginResponseFromServer;

    if (result.data?.login.error != null) {
      return {
        error: result.data.login.error
      };
    }
    if (result.error != null) {
      return {
        error: result.error
      };
    }
    const token = result.data?.login.token;

    if (!token || !result.data?.login.username) {
      return {
        error: 'Unauthorized'
      };
    }

    const expiryAt = result.data?.login.expiryAt ?? '';
    // console.log(`Got token: ${token}`);
    const userId = result.data.login.userId ?? '';
    const email = result.data.login.email ?? '';
    const groups = result.data.login.groups ?? [];
    this.setUserLoggedIn({
      username: result.data.login.username,
      userId,
      email,
      groups,
      token: token,
      expiryAt: expiryAt
    })
    this.backendService.setAuthToken(token);
    return {
      token,
      expiryAt,
      username,
      userId,
      email,
      groups
    }
  }

}
